import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Sparkles, Check, ShoppingBag, Laptop, ArrowRight, Trophy, Loader2 } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Start-of-shift ritual. Renders in 3 steps:
 *   1. Mode picker  (online / offline)
 *   2. Checklist    (all "required" items must be ticked)
 *   3. Goal + reward
 *
 * Skill/story of the day is fetched once and shown at the end as a
 * banner — the manager acknowledges it inside the app during the day.
 */
export default function ShiftStartModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [mode, setMode] = useState(null);
  const [meta, setMeta] = useState(null);   // {online:{start,end}, offline:{...}}
  const [done, setDone] = useState(new Set());
  const [goal, setGoal] = useState({ sales_count: "", revenue: "", reward: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [tips, setTips] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/shift/checklists`, { withCredentials: true })
      .then(r => setMeta(r.data)).catch(() => {});
    axios.get(`${API}/admin/shift/tips-today`, { withCredentials: true })
      .then(r => setTips(r.data)).catch(() => {});
  }, []);

  const items = useMemo(() => {
    if (!mode || !meta) return [];
    return meta[mode]?.start || [];
  }, [mode, meta]);

  const requiredIds = useMemo(() => items.filter(i => i.required).map(i => i.id), [items]);
  const missingRequired = requiredIds.some(id => !done.has(id));

  const toggle = (id) => setDone(prev => {
    const n = new Set(prev);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const submit = async () => {
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/admin/shift/start`, {
        mode,
        goal: {
          sales_count: Number(goal.sales_count) || 0,
          revenue: Number(goal.revenue) || 0,
          reward: goal.reward || "",
        },
        start_checklist_done: Array.from(done),
      }, { withCredentials: true });
      onClose(true);
    } catch (e) {
      setError(e.response?.data?.detail || "Не удалось начать смену");
      setSaving(false);
    }
  };

  return (
    <div style={backdrop} onClick={() => onClose(false)}>
      <div data-testid="shift-start-modal" onClick={e => e.stopPropagation()} style={modal}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontFamily: "Cormorant Garamond", fontSize: 24, color: "#0A0A0A" }}>
            {step === 1 && "Начало смены"}
            {step === 2 && "Настрой на результат"}
            {step === 3 && "Моя цель сегодня"}
          </div>
          <button onClick={() => onClose(false)} style={closeBtn}><X size={18} /></button>
        </div>

        {/* Step indicator */}
        <StepDots current={step} total={3} />

        {/* Step 1 — Mode picker */}
        {step === 1 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#64748b", marginBottom: 16 }}>
              Выберите формат смены — от этого зависит чек-лист подготовки.
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <ModeCard testid="shift-mode-online" active={mode === "online"}
                icon={Laptop} title="ONLINE" subtitle="Работа с заявками"
                onClick={() => setMode("online")} />
              <ModeCard testid="shift-mode-offline" active={mode === "offline"}
                icon={ShoppingBag} title="OFFLINE" subtitle="Работа в бутике"
                onClick={() => setMode("offline")} />
            </div>
            <BottomBar>
              <span />
              <button data-testid="shift-next-1" onClick={() => setStep(2)}
                disabled={!mode} style={primaryBtn(!mode)}>
                Далее <ArrowRight size={13} />
              </button>
            </BottomBar>
          </div>
        )}

        {/* Step 2 — Checklist */}
        {step === 2 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b", marginBottom: 12 }}>
              Отметьте {mode === "online" ? "то, что вы сделали" : "готовность бутика"}. Пункты со звёздочкой обязательны.
            </div>
            {items.map((it) => (
              <label key={it.id} data-testid={`shift-check-${it.id}`}
                onClick={() => toggle(it.id)}
                style={{
                  display: "flex", alignItems: "flex-start", gap: 10,
                  padding: "10px 12px", marginBottom: 6, borderRadius: 8,
                  border: done.has(it.id) ? "1px solid #C5A059" : "1px solid #e5e7eb",
                  background: done.has(it.id) ? "rgba(197,160,89,0.05)" : "white",
                  cursor: "pointer",
                }}>
                <div style={{
                  width: 20, height: 20, borderRadius: 4, marginTop: 1,
                  border: "2px solid " + (done.has(it.id) ? "#C5A059" : "#d1d5db"),
                  background: done.has(it.id) ? "#C5A059" : "white",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  flexShrink: 0,
                }}>
                  {done.has(it.id) && <Check size={14} color="white" />}
                </div>
                <div style={{ fontFamily: "Manrope", fontSize: 13, color: "#0A0A0A", lineHeight: 1.4 }}>
                  {it.text} {it.required && <span style={{ color: "#DC2626" }}>*</span>}
                </div>
              </label>
            ))}
            <BottomBar>
              <button data-testid="shift-back-2" onClick={() => setStep(1)} style={secondaryBtn}>
                Назад
              </button>
              <button data-testid="shift-next-2" onClick={() => setStep(3)}
                disabled={missingRequired}
                title={missingRequired ? "Отметьте все обязательные пункты" : ""}
                style={primaryBtn(missingRequired)}>
                Далее <ArrowRight size={13} />
              </button>
            </BottomBar>
          </div>
        )}

        {/* Step 3 — Goal + reward + tips preview */}
        {step === 3 && (
          <div style={{ marginTop: 12 }}>
            <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b", marginBottom: 12 }}>
              {mode === "offline"
                ? "Ориентир: минимум 1 000 000 ₸ выручки ИЛИ 3 продажи за смену."
                : "Определите конкретные цифры на день. Их не увидит никто, кроме вас — но при закрытии смены пойдут в отчёт."}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <NumField testid="shift-goal-sales" label="Количество продаж"
                value={goal.sales_count} onChange={v => setGoal({ ...goal, sales_count: v })}
                placeholder="3" />
              <NumField testid="shift-goal-revenue" label="Выручка, ₸"
                value={goal.revenue} onChange={v => setGoal({ ...goal, revenue: v })}
                placeholder="1500000" />
            </div>
            {mode === "offline" && (
              <div style={{ marginTop: 10 }}>
                <div style={lbl}>🎁 Как порадую себя, если выполню план</div>
                <input data-testid="shift-goal-reward" type="text" value={goal.reward}
                  onChange={e => setGoal({ ...goal, reward: e.target.value })}
                  placeholder="Вкусный ужин / любимый десерт / фильм"
                  style={inp} />
              </div>
            )}

            {/* Tips of the day */}
            {tips && (tips.skill || tips.story) && (
              <div style={{ marginTop: 16, background: "#fef3c7",
                             border: "1px solid #fde68a", borderRadius: 8, padding: 12 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6 }}>
                  <Sparkles size={13} color="#B45309" />
                  <div style={{ fontFamily: "Manrope", fontSize: 11, color: "#B45309",
                                 fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>
                    Тренировка дня
                  </div>
                </div>
                {tips.skill && (
                  <div style={{ marginBottom: 8 }}>
                    <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 13, color: "#0A0A0A" }}>
                      Возражение: {tips.skill.title}
                    </div>
                    <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#374151", marginTop: 4, lineHeight: 1.5 }}>
                      {tips.skill.hint} <br />
                      <i style={{ color: "#7C2D12" }}>{tips.skill.phrase}</i><br />
                      <span style={{ fontSize: 11, color: "#6b7280" }}>
                        Использовать сегодня минимум {tips.skill.target || 3} раза.
                      </span>
                    </div>
                  </div>
                )}
                {tips.story && (
                  <div>
                    <div style={{ fontFamily: "Manrope", fontWeight: 700, fontSize: 13, color: "#0A0A0A" }}>
                      История: {tips.story.title}
                    </div>
                    <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#374151", marginTop: 4, lineHeight: 1.5 }}>
                      {tips.story.text}
                    </div>
                  </div>
                )}
              </div>
            )}

            {error && (
              <div style={{ marginTop: 12, padding: "8px 12px", background: "#fef2f2",
                             border: "1px solid #fecaca", borderRadius: 6,
                             color: "#7f1d1d", fontFamily: "Manrope", fontSize: 12 }}>
                {error}
              </div>
            )}

            <BottomBar>
              <button data-testid="shift-back-3" onClick={() => setStep(2)} style={secondaryBtn}>
                Назад
              </button>
              <button data-testid="shift-start-btn" onClick={submit} disabled={saving}
                style={primaryBtn(saving)}>
                {saving ? <Loader2 size={13} className="animate-spin" /> : <Trophy size={13} />}
                Цель поставлена · Начать смену
              </button>
            </BottomBar>
          </div>
        )}
      </div>
    </div>
  );
}

/* ---- atoms ---- */
function ModeCard({ active, icon: Icon, title, subtitle, onClick, testid }) {
  return (
    <button data-testid={testid} onClick={onClick}
      style={{
        padding: "20px 16px", borderRadius: 10,
        border: active ? "2px solid #C5A059" : "2px solid #e5e7eb",
        background: active ? "linear-gradient(135deg, rgba(197,160,89,0.08), rgba(212,184,120,0.15))" : "white",
        cursor: "pointer", textAlign: "left",
        display: "flex", flexDirection: "column", gap: 8,
      }}>
      <Icon size={22} color={active ? "#8a6d2a" : "#64748b"} />
      <div style={{ fontFamily: "Cormorant Garamond", fontSize: 20, fontWeight: 600, color: "#0A0A0A" }}>
        {title}
      </div>
      <div style={{ fontFamily: "Manrope", fontSize: 12, color: "#64748b" }}>
        {subtitle}
      </div>
    </button>
  );
}

function NumField({ label, value, onChange, placeholder, testid }) {
  return (
    <label style={{ display: "block" }}>
      <div style={lbl}>{label}</div>
      <input data-testid={testid} type="number" min={0} value={value}
        onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={inp} />
    </label>
  );
}

function StepDots({ current, total }) {
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 4 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{
          height: 3, flex: 1, borderRadius: 2,
          background: (i + 1) <= current ? "#C5A059" : "#e5e7eb",
          transition: "background 0.2s",
        }} />
      ))}
    </div>
  );
}

function BottomBar({ children }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20, gap: 8 }}>
      {children}
    </div>
  );
}

const backdrop = {
  position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 200,
  display: "flex", alignItems: "flex-start", justifyContent: "center",
  padding: "24px 16px", overflowY: "auto",
};
const modal = {
  background: "white", borderRadius: 12, maxWidth: 560, width: "100%",
  padding: 24, margin: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
};
const closeBtn = { background: "none", border: "none", cursor: "pointer", color: "#888" };
const inp = { width: "100%", background: "white", border: "1px solid #e5e7eb",
              borderRadius: 6, padding: "9px 11px", fontFamily: "Manrope", fontSize: 13,
              outline: "none" };
const lbl = { display: "block", fontFamily: "Manrope", fontSize: 11, fontWeight: 700,
              color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em",
              marginBottom: 5 };
const secondaryBtn = { background: "white", border: "1px solid #d1d5db", borderRadius: 6,
                        padding: "9px 16px", cursor: "pointer",
                        fontFamily: "Manrope", fontSize: 12, fontWeight: 600, color: "#374151" };
const primaryBtn = (disabled) => ({
  background: disabled ? "#e5e7eb" : "linear-gradient(135deg,#C5A059,#D4B878)",
  color: "#0A0A0A", border: "none", borderRadius: 6, padding: "9px 18px",
  cursor: disabled ? "not-allowed" : "pointer",
  fontFamily: "Manrope", fontSize: 12, fontWeight: 700,
  display: "inline-flex", alignItems: "center", gap: 6,
});
